import { inject, Injectable, NgZone } from '@angular/core';
import {
    DefaultSortingStrategy,
    FilteringExpressionsTree,
    IFilteringExpression,
    IFilteringExpressionsTree,
    IgxColumnComponent,
    IgxGridComponent
} from '@infragistics/igniteui-angular';
import { Observable, of } from 'rxjs';
import { mergeMap, take } from 'rxjs/operators';

import { GridColumnConfig } from '../components/paged-grid/models/grid-column.config';
import { PagedGridComponent } from '../components/paged-grid/paged-grid.component';
import { ApiResponseModel } from '../models/core/api-response.model';
import { GridStoredConfig } from '../models/core/grid-stored.config';
import { RolesResource } from './resources/roles.resource';
import { SecurityStorage } from './storage/security.storage';
import { SettingsSessionStorage } from './storage/settings.session-storage';

@Injectable({
    providedIn: 'root'
})
export class GridSettingsService {
    shouldPreventOnBeforeDestroySave = false;

    private readonly settingsSessionStorage = inject(SettingsSessionStorage);
    private readonly securityStorage = inject(SecurityStorage);
    private readonly rolesResource = inject(RolesResource);
    private readonly ngZone = inject(NgZone);

    save(gridName: string, grid: IgxGridComponent): void {
        const config = this.createConfigForGrid(grid);
        const settings = this.settingsSessionStorage.getUserGridsConfiguration();

        settings.setGridConfig(gridName, config);
        this.settingsSessionStorage.saveUserGridsConfiguration(settings);
    }

    load(gridName: string, pagedGrid: PagedGridComponent<any>): Observable<boolean> {
        const userConfig = this.settingsSessionStorage.getUserGridsConfiguration();
        const config = !!userConfig && !!userConfig.grids ? userConfig.grids[gridName] : undefined;

        if (!!config) {
            this.loadFromConfig(config, pagedGrid);
        } else {
            return this.loadForRole(this.securityStorage.getUserInfo()?.roleId ?? 0, gridName);
        }

        return of(true);
    }

    saveForRole(roleId: number, gridName: string, grid: IgxGridComponent): Observable<ApiResponseModel<boolean>> {
        const config = this.createConfigForGrid(grid);
        return this.rolesResource.saveGridDefaultSettings(config, roleId, gridName);
    }

    loadForRole(roleId: number, gridName: string): Observable<boolean> {
        return this.rolesResource.getGridDefaultSettings(roleId, gridName).pipe(
            mergeMap(config => {
                if (!!config) {
                    this.ngZone.onStable.pipe(take(1)).subscribe(() => {
                        this.shouldPreventOnBeforeDestroySave = true;
                        const userConfig = this.settingsSessionStorage.getUserGridsConfiguration();
                        userConfig.grids[gridName] = config;

                        this.settingsSessionStorage.saveUserGridsConfiguration(userConfig);
                        location.reload();
                    });
                    return of(true);
                } else {
                    return of(false);
                }
            })
        );
    }

    remove(gridName: string): void {
        const userConfig = this.settingsSessionStorage.getUserGridsConfiguration();
        const config = !!userConfig && !!userConfig.grids ? userConfig.grids[gridName] : undefined;

        if (!!config) {
            delete userConfig.grids[gridName];
            this.settingsSessionStorage.saveUserGridsConfiguration(userConfig);
        }
    }

    private loadFromConfig(config: GridStoredConfig, pagedGrid: PagedGridComponent<any>): void {
        if (!!config.columns && config.columns.length > 0) {
            pagedGrid.resetColumns(config.columns);
        }

        if (!!config.filters) {
            pagedGrid.grid().filteringExpressionsTree = this.setupFilterTree(pagedGrid.grid(), config.filters);
        }

        if (!!config.paging) {
            if (pagedGrid.grid().perPage !== config.paging.recordsPerPage) {
                pagedGrid.grid().perPage = config.paging.recordsPerPage;
            }

            if (pagedGrid.grid().page !== config.paging.index) {
                pagedGrid.grid().paginator?.paginate(config.paging.index);
            }
        }

        if (!!config.sorting) {
            const strategy = DefaultSortingStrategy.instance();
            config.sorting.forEach(expr => (expr.strategy = strategy));
            pagedGrid.grid().sortingExpressions = config.sorting;
        }

        if (!!config.selection) {
            pagedGrid.grid().selectRows(config.selection, true);
            /**
             * This "work around" was required because ignite's grid is not firing onRowSelectionChange
             * when we call selectRows. Not even forcing the grid's CDR to detect changes.
             */
            pagedGrid.grid().rowSelectionChanging.next({
                newSelection: config.selection,
                added: undefined!,
                cancel: undefined!,
                oldSelection: undefined!,
                removed: undefined!
            });
        }

        pagedGrid.grid().cdr.detectChanges();
    }

    private setupColumns(columns: IgxColumnComponent[]): GridColumnConfig[] {
        const columnsConfig: GridColumnConfig[] = [];

        columns.forEach(c =>
            columnsConfig.push(
                new GridColumnConfig({
                    isPinned: c.pinned,
                    isHidden: c.hidden,
                    width: c.width,
                    fieldName: c.field,
                    index: c.visibleIndex
                })
            )
        );

        return columnsConfig;
    }

    private setupFilterTree(grid: IgxGridComponent, expTree: IFilteringExpressionsTree): FilteringExpressionsTree {
        const gridFilteringExpressionsTree = new FilteringExpressionsTree(expTree.operator);

        for (const filterOperand of expTree.filteringOperands) {
            const filtOperand = filterOperand as FilteringExpressionsTree;
            let colsFilterOp: any;

            if (filtOperand.filteringOperands.length > 1) {
                colsFilterOp = filtOperand.filteringOperands as IFilteringExpression[];
            } else {
                colsFilterOp = filtOperand.filteringOperands[0] as IFilteringExpression;

                if (Array.isArray(colsFilterOp.filteringOperands)) {
                    colsFilterOp = colsFilterOp.filteringOperands;
                } else {
                    colsFilterOp = [colsFilterOp];
                }
            }

            const columnFilteringExpressionsTree = this.createExpressionsTree(grid, colsFilterOp, filtOperand);

            gridFilteringExpressionsTree.filteringOperands.push(columnFilteringExpressionsTree);
        }

        return gridFilteringExpressionsTree;
    }

    private createExpressionsTree(
        grid: IgxGridComponent,
        colsFilterOp: IFilteringExpression[],
        filterOps: FilteringExpressionsTree
    ): FilteringExpressionsTree {
        const colFilteringExpressionsTree = new FilteringExpressionsTree(filterOps.operator, filterOps.fieldName);
        const column = grid.columns.filter(col => col.field === filterOps.fieldName)[0];

        for (const filterOperand of colsFilterOp) {
            const colFilterOp = filterOperand as IFilteringExpression;

            if (!colFilterOp.condition) {
                continue;
            }

            colFilterOp.condition = column.filters.condition(colFilterOp.condition.name);

            if (column.dataType === 'date') {
                colFilterOp.searchVal = new Date(Date.parse(colFilterOp.searchVal));
            }

            colFilteringExpressionsTree.filteringOperands.push(colFilterOp);
        }

        return colFilteringExpressionsTree;
    }

    private createConfigForGrid(grid: IgxGridComponent): GridStoredConfig {
        return new GridStoredConfig({
            paging: grid.pagingState,
            sorting: grid.sortingExpressions,
            filters: grid.filteringExpressionsTree.filteringOperands.length > 0 ? grid.filteringExpressionsTree : undefined,
            selection: grid.selectedRows,
            columns: this.setupColumns(grid.columns)
        });
    }
}
